import { type ViewStateContract } from "@wags/wags-api-client/session-state.contract.ts";
import { createServerFn } from "@tanstack/react-start";

import { resolveViewerRef } from "@/lib/core/auth";
import { getKitsTelemetry, getTelemetry } from "@/lib/core/telemetry.server.ts";
import { getViewStateStore } from "@/lib/core/view-state-store.server.ts";
import type IPublishViewStateDependencies from "./publish-view-state-dependencies.interface";
import {
    publishViewStateRequestSchema,
    type PublishViewStateResult,
} from "./view-state.contract";

const COARSE_LOCATION_KEYS = ["lat", "lng", "latitude", "longitude"];

const MAX_VIEWSTATE_CHARS = 8_000;

function coarsenQueryLocation(key: string, value: string): string {
    if (!COARSE_LOCATION_KEYS.includes(key) || value.trim() === "")
        return value;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric.toFixed(2) : value;
}

function coarsenLocationFields(
    fields: ViewStateContract["slices"],
): ViewStateContract["slices"] {
    return Object.fromEntries(
        Object.entries(fields).map(([key, value]) => {
            if (COARSE_LOCATION_KEYS.includes(key)) {
                if (typeof value === "number")
                    return [key, Math.round(value * 100) / 100];
                if (typeof value === "string")
                    return [key, coarsenQueryLocation(key, value)];
            }
            return [key, coarsenLocationDeep(value)];
        }),
    );
}

function coarsenLocationDeep(
    value: ViewStateContract["slices"][string],
): ViewStateContract["slices"][string] {
    if (Array.isArray(value)) return value.map(coarsenLocationDeep);
    if (value && typeof value === "object") return coarsenLocationFields(value);
    return value;
}

export function coarsenViewStateForAnalytics(
    state: ViewStateContract,
): ViewStateContract {
    return {
        ...state,
        query: Object.fromEntries(
            Object.entries(state.query).map(([key, value]) => [
                key,
                coarsenQueryLocation(key, value),
            ]),
        ),
        slices: coarsenLocationFields(state.slices),
    };
}

export async function handlePublish(
    deps: IPublishViewStateDependencies,
    state: ViewStateContract,
): Promise<PublishViewStateResult["status"]> {
    const viewer = await deps.resolveViewer();
    if (!viewer?.viewerId) return "skipped";
    if (JSON.stringify(state).length > MAX_VIEWSTATE_CHARS) return "oversized";
    const result = await deps.store.write(viewer.viewerId, state);

    if (result === "written") {
        deps.emit(coarsenViewStateForAnalytics(state));
        return "written";
    }
    return result === "error" ? "error" : "skipped";
}

export const publishKitsViewState = createServerFn({ method: "POST" })
    .inputValidator(publishViewStateRequestSchema)
    .handler(async ({ data }) => {
        const status = await handlePublish(
            {
                resolveViewer: async () => {
                    const ref = await resolveViewerRef();
                    return ref?.viewerId ? { viewerId: ref.viewerId } : null;
                },
                store: getViewStateStore(),
                emit: (coarsened) => {
                    void getKitsTelemetry()
                        .then((t) => t.emit("kits.view_state", coarsened))
                        .catch(() => {});
                },
            },
            data.state,
        );
        if (status === "oversized" || status === "error") {
            void getTelemetry()
                .then((t) =>
                    t.log.warn(`kits.view_state_publish_${status}`, {
                        page: data.state.page,
                    }),
                )
                .catch(() => {});
        }
        return {
            ok: status !== "error",
            status,
        } satisfies PublishViewStateResult;
    });
